import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaCalendarAlt,
  FaUsers,
  FaClock,
  FaEdit,
  FaCheckCircle,
  FaTimesCircle,
  FaUserPlus,
  FaClipboardList,
  FaTimes,
  FaStar,
  FaHeart,
  FaDownload,
  FaRegCircle,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BackButton from '../components/BackButton';
import PortalBackground from '../components/PortalBackground';

const PositionHiringOverview = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { positionId } = useParams();
  const navigate = useNavigate();
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [activeTab, setActiveTab] = useState('timeline');
  const [position, setPosition] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState([]);
  const [timelineSteps, setTimelineSteps] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditDescriptionModal, setShowEditDescriptionModal] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [candidateRatings, setCandidateRatings] = useState({
    overall: 0,
    skill: 0,
    knowledge: 0,
    experience: 0,
    requirements: 0,
    culture: 0
  });
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  // Candidate hiring-process steps (mirrors the position timeline UX)
  const [candidateProcessSteps, setCandidateProcessSteps] = useState({});
  const [showCandidateProcessModal, setShowCandidateProcessModal] = useState(false);
  const [selectedCandidateStep, setSelectedCandidateStep] = useState(null);
  const [candidateStepCompletionDate, setCandidateStepCompletionDate] = useState('');

  // Fetch position data
  useEffect(() => {
    const fetchPositionData = async () => {
      if (!currentUser || !positionId) return;

      try {
        setLoading(true);
        setError(null);

        // First, get the position to find the positionRequestId
        const positionDoc = await getDoc(doc(db, 'positions', positionId));
        if (!positionDoc.exists()) {
          setError('Position not found');
          setLoading(false);
          return;
        }

        const positionData = positionDoc.data();
        
        // Check if position belongs to current user
        if (positionData.userId !== currentUser.uid) {
          setError('Access denied');
          setLoading(false);
          return;
        }

        // If there's a positionRequestId, fetch the position request data for notes/candidates/timeline
        let requestData = null;
        if (positionData.positionRequestId) {
          const requestDoc = await getDoc(doc(db, 'positionRequests', positionData.positionRequestId));
          if (requestDoc.exists()) {
            requestData = requestDoc.data();
          }
        }

        // Calculate days posted from "Create Posting" timeline step completion date
        let daysPosted = 'N/A';
        if (requestData?.timelineSteps?.['create-posting']?.completedDate) {
          const postedDate = new Date(requestData.timelineSteps['create-posting'].completedDate);
          daysPosted = Math.floor((new Date() - postedDate) / (1000 * 60 * 60 * 24));
        }

        setPosition({
          id: positionId,
          title: positionData.title,
          status: positionData.status || 'Active',
          applications: positionData.applications || 0,
          daysPosted,
          department: positionData.department || 'TBD',
          location: positionData.location || 'TBD',
          type: positionData.type || 'Full-time',
          priority: positionData.priority || 'Normal',
          description: positionData.description || '',
          budget: positionData.budget || '',
          positionRequestId: positionData.positionRequestId,
          ...positionData
        });

        // Load existing notes from position request data (if available)
        setNotes(requestData?.notes || []);

        // Load candidates from position request data (if available)
        setCandidates(requestData?.candidates || []);

        // Load existing timeline steps from position request data (if available)
        setTimelineSteps(requestData?.timelineSteps || {});

        setLoading(false);
      } catch (error) {
        console.error('Error fetching position data:', error);
        setError('Failed to load position data');
        setLoading(false);
      }
    };

    fetchPositionData();
  }, [currentUser, positionId]);

  // Timeline state
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [stepCompletionDate, setStepCompletionDate] = useState('');

  const handleBack = () => {
    navigate('/portal/hr/hiring');
  };

  // Handle cancel position
  const handleCancelPosition = async () => {
    try {
      // Delete the position
      const positionRef = doc(db, 'positions', positionId);
      await deleteDoc(positionRef);
      
      // If there's an associated position request, delete it too
      if (position?.positionRequestId) {
        const requestRef = doc(db, 'positionRequests', position.positionRequestId);
        await deleteDoc(requestRef);
      }
      
      // Navigate back to hiring page
      navigate('/portal/hr/hiring');
    } catch (error) {
      console.error('Error canceling position:', error);
    }
  };

  // Handle edit description
  const handleEditDescription = () => {
    setEditDescription(position?.description || '');
    setShowEditDescriptionModal(true);
  };

  const handleSaveDescription = async () => {
    try {
      const positionRef = doc(db, 'positions', positionId);
      await updateDoc(positionRef, {
        description: editDescription,
        updatedAt: new Date().toISOString()
      });
      
      setPosition(prev => ({
        ...prev,
        description: editDescription
      }));
      
      setShowEditDescriptionModal(false);
    } catch (error) {
      console.error('Error updating description:', error);
    }
  };

  const handleCancelEditDescription = () => {
    setEditDescription('');
    setShowEditDescriptionModal(false);
  };

  // Helper functions for candidates
  const getCandidateStage = (candidate) => {
    const processSteps = candidate.processSteps || {};
    const stepOrder = ['applied', 'pre-screen-interview', 'company-interview', 'decision-offer'];
    
    for (let i = stepOrder.length - 1; i >= 0; i--) {
      const step = stepOrder[i];
      if (processSteps[step]?.completed) {
        return step.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
    }
    
    return 'Applied';
  };

  const getCandidateScore = (candidate) => {
    const ratings = candidate.ratings || {};
    const overallScore = ratings.overall || 0;
    return `${overallScore}/5`;
  };

  // Render star rating component
  const renderStarRating = (category, currentRating) => {
    return (
      <div className="flex space-x-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => handleRatingClick(category, star)}
            className={`transition-colors duration-200 ${
              star <= currentRating 
                ? 'text-yellow-400 hover:text-yellow-500' 
                : 'text-gray-300 hover:text-yellow-400'
            }`}
          >
            <FaStar className="w-5 h-5" />
          </button>
        ))}
      </div>
    );
  };

  const handleRatingClick = async (category, rating) => {
    if (!selectedCandidate) return;

    const updatedRatings = {
      ...candidateRatings,
      [category]: rating
    };

    setCandidateRatings(updatedRatings);

    // Update candidate in the candidates array
    const updatedCandidates = candidates.map(c => 
      c.id === selectedCandidate.id 
        ? { ...c, ratings: { ...c.ratings, [category]: rating } }
        : c
    );
    setCandidates(updatedCandidates);

    // Update selected candidate
    setSelectedCandidate({
      ...selectedCandidate,
      ratings: { ...selectedCandidate.ratings, [category]: rating }
    });

    // Save to Firestore
    await saveCandidatesToFirestore(updatedCandidates);
  };

  const handleToggleFavorite = async (candidateId) => {
    const updatedCandidates = candidates.map(c => {
      if (c.id !== candidateId) return c;
      const nowFavorited = !c.isFavorite;
      return {
        ...c,
        isFavorite: nowFavorited,
        favoritedAt: nowFavorited ? new Date().toISOString() : null,
        favoritedBy: nowFavorited ? (currentUser?.uid || null) : null,
      };
    });
    setCandidates(updatedCandidates);

    // Update selected candidate if it's the same one
    if (selectedCandidate && selectedCandidate.id === candidateId) {
      setSelectedCandidate({ ...selectedCandidate, isFavorite: !selectedCandidate.isFavorite });
    }
    
    // Save to Firestore
    await saveCandidatesToFirestore(updatedCandidates);
  };

  // Save notes to Firestore
  const saveNotesToFirestore = async (updatedNotes) => {
    if (!position?.positionRequestId) return;

    try {
      const requestRef = doc(db, 'positionRequests', position.positionRequestId);
      await updateDoc(requestRef, {
        notes: updatedNotes,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving notes to Firestore:', error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      return; // Don't add empty notes
    }

    const currentDate = new Date();
    const formattedDate = `${currentDate.getMonth() + 1}/${currentDate.getDate()} ${currentDate.getHours() > 12 ? currentDate.getHours() - 12 : currentDate.getHours()}:${currentDate.getMinutes().toString().padStart(2, '0')}${currentDate.getHours() >= 12 ? 'PM' : 'AM'}`;

    // Get user display name from profile data
    const getUserDisplayName = async () => {
      if (!currentUser) return 'Current User';
      
      try {
        // Try to get from users collection first (portal users)
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          if (fullName) {
            return fullName;
          }
        }
        
        // If not found, try internal_users collection (CRM users)
        const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (internalUserDoc.exists()) {
          const internalUserData = internalUserDoc.data();
          const fullName = `${internalUserData.firstName || ''} ${internalUserData.lastName || ''}`.trim();
          if (fullName) {
            return fullName;
          }
        }
        
        // Fallback to display name
        if (currentUser.displayName) {
          return currentUser.displayName;
        }
        
        // Last fallback: extract name from email
        if (currentUser.email) {
          const emailName = currentUser.email.split('@')[0];
          return emailName
            .replace(/[._-]/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }
        
        return 'Current User';
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fallback to display name or email
        return currentUser.displayName || currentUser.email?.split('@')[0] || 'Current User';
      }
    };

    // Get user profile image
    const getUserProfileImage = async () => {
      if (!currentUser) return null;
      
      try {
        // Try to get from users collection first (portal users)
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.profileImage) {
            return userData.profileImage;
          }
        }
        
        // If not found, try internal_users collection (CRM users)
        const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (internalUserDoc.exists()) {
          const internalUserData = internalUserDoc.data();
          if (internalUserData.profileImage) {
            return internalUserData.profileImage;
          }
        }
        
        return currentUser.photoURL || null;
      } catch (error) {
        console.error('Error fetching user profile image:', error);
        return currentUser.photoURL || null;
      }
    };

    const displayName = await getUserDisplayName();
    const profileImage = await getUserProfileImage();

    const newNoteItem = {
      id: Date.now(),
      content: newNote,
      author: {
        name: displayName,
        photoURL: profileImage,
        uid: currentUser?.uid || null
      },
      timestamp: new Date().toISOString(),
      date: formattedDate
    };

    const updatedNotes = [newNoteItem, ...notes]; // Add new note at the beginning
    setNotes(updatedNotes);
    setNewNote('');
    setShowAddNoteModal(false);
    
    // Save to Firestore
    await saveNotesToFirestore(updatedNotes);
  };

  const handleDeleteNote = async (noteId) => {
    const updatedNotes = notes.filter(note => note.id !== noteId);
    setNotes(updatedNotes);
    
    // Save to Firestore
    await saveNotesToFirestore(updatedNotes);
  };

  // Save timeline steps to Firestore
  const saveTimelineStepsToFirestore = async (updatedTimelineSteps) => {
    if (!position?.positionRequestId) return;

    try {
      const requestRef = doc(db, 'positionRequests', position.positionRequestId);
      await updateDoc(requestRef, {
        timelineSteps: updatedTimelineSteps,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving timeline steps to Firestore:', error);
    }
  };

  // Handle timeline step completion
  const handleTimelineStepClick = (stepId) => {
    const stepData = timelineSteps[stepId];
    const isCompleted = stepData?.completed;
    
    if (isCompleted) {
      // If already completed, mark as incomplete directly
      handleMarkStepIncomplete(stepId);
    } else {
      // If not completed, show modal to mark as complete
      setSelectedStep(stepId);
      setStepCompletionDate('');
      setShowTimelineModal(true);
    }
  };

  // Handle marking timeline step as complete
  const handleMarkStepComplete = async () => {
    if (!selectedStep || !stepCompletionDate) {
      return;
    }

    const updatedTimelineSteps = {
      ...timelineSteps,
      [selectedStep]: {
        completed: true,
        completedDate: stepCompletionDate,
        completedAt: new Date().toISOString()
      }
    };

    setTimelineSteps(updatedTimelineSteps);
    setShowTimelineModal(false);
    setSelectedStep(null);
    setStepCompletionDate('');

    // Save to Firestore
    await saveTimelineStepsToFirestore(updatedTimelineSteps);
  };

  // Handle marking timeline step as incomplete
  const handleMarkStepIncomplete = async (stepId) => {
    const updatedTimelineSteps = {
      ...timelineSteps,
      [stepId]: {
        completed: false,
        completedDate: null,
        completedAt: null
      }
    };

    setTimelineSteps(updatedTimelineSteps);

    // Save to Firestore
    await saveTimelineStepsToFirestore(updatedTimelineSteps);
  };

  const handleCandidateClick = (candidate) => {
    setSelectedCandidate(candidate);
    setCandidateRatings(candidate.ratings || {
      overall: 0,
      skill: 0,
      knowledge: 0,
      experience: 0,
      requirements: 0,
      culture: 0
    });
    setCandidateProcessSteps(candidate.processSteps || {});

    // Set the selected action based on candidate's current status
    if (candidate.actionStatus === 'rejected') {
      setSelectedAction('reject');
    } else if (candidate.actionStatus === 'continue') {
      setSelectedAction('continue');
    } else {
      setSelectedAction(null);
    }
    
    setShowCandidateModal(true);
  };

  // Handle feedback modal
  const handleOpenFeedback = () => {
    setFeedbackRating(selectedCandidate?.feedbackRating || 0);
    setFeedbackNote(selectedCandidate?.feedbackNote || '');
    setShowFeedbackModal(true);
  };

  const handleSaveFeedback = async () => {
    if (!selectedCandidate) return;

    const updatedCandidates = candidates.map(c => 
      c.id === selectedCandidate.id 
        ? { 
            ...c, 
            feedbackRating: feedbackRating,
            feedbackNote: feedbackNote,
            feedbackUpdatedAt: new Date().toISOString()
          }
        : c
    );
    setCandidates(updatedCandidates);
    
    // Update selected candidate
    setSelectedCandidate({
      ...selectedCandidate,
      feedbackRating: feedbackRating,
      feedbackNote: feedbackNote,
      feedbackUpdatedAt: new Date().toISOString()
    });
    
    // Save to Firestore
    await saveCandidatesToFirestore(updatedCandidates);
    
    setShowFeedbackModal(false);
  };

  const handleCancelFeedback = () => {
    setFeedbackRating(0);
    setFeedbackNote('');
    setShowFeedbackModal(false);
  };

  // Handle action selection
  const handleActionSelect = async (action) => {
    if (!selectedCandidate) return;
    
    const newSelectedAction = selectedAction === action ? null : action;
    setSelectedAction(newSelectedAction);
    
    // If an action is selected, save it to Firestore
    if (newSelectedAction) {
      let updateData = {};
      
      if (newSelectedAction === 'reject') {
        updateData = {
          isRejected: true,
          rejectedAt: new Date().toISOString(),
          actionStatus: 'rejected'
        };
      } else if (newSelectedAction === 'continue') {
        updateData = {
          isRejected: false,
          actionStatus: 'continue',
          continuedAt: new Date().toISOString()
        };
      }
      
      // Update candidates array
      const updatedCandidates = candidates.map(c => 
        c.id === selectedCandidate.id 
          ? { ...c, ...updateData }
          : c
      );
      setCandidates(updatedCandidates);
      
      // Update selected candidate
      setSelectedCandidate({
        ...selectedCandidate,
        ...updateData
      });
      
      // Save to Firestore
      await saveCandidatesToFirestore(updatedCandidates);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    if (!position) return;

    try {
      const positionRef = doc(db, 'positions', positionId);
      await updateDoc(positionRef, {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });

      // Keep the linked position request (CRM HR Fulfillment) in sync
      if (position.positionRequestId) {
        const requestStatus =
          newStatus === 'In Progress' ? 'in-progress' :
          newStatus === 'Completed' ? 'completed' : 'pending';
        const requestRef = doc(db, 'positionRequests', position.positionRequestId);
        await updateDoc(requestRef, {
          status: requestStatus,
          updatedAt: new Date().toISOString()
        });
      }

      setPosition(prev => ({
        ...prev,
        status: newStatus
      }));

      setShowStatusModal(false);
    } catch (error) {
      console.error('Error updating position status:', error);
    }
  };

  // Save candidates to Firestore
  const saveCandidatesToFirestore = async (updatedCandidates) => {
    if (!position?.positionRequestId) return;

    try {
      const requestRef = doc(db, 'positionRequests', position.positionRequestId);
      await updateDoc(requestRef, {
        candidates: updatedCandidates,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving candidates to Firestore:', error);
    }
  };

  // Save a candidate's process steps to Firestore (on the linked request)
  const saveCandidateProcessStepsToFirestore = async (candidateId, updatedProcessSteps) => {
    if (!position?.positionRequestId || !candidateId) return;

    try {
      const updatedCandidates = candidates.map((candidate) =>
        candidate.id === candidateId ? { ...candidate, processSteps: updatedProcessSteps } : candidate
      );
      const requestRef = doc(db, 'positionRequests', position.positionRequestId);
      await updateDoc(requestRef, {
        candidates: updatedCandidates,
        updatedAt: new Date().toISOString()
      });
      setCandidates(updatedCandidates);
    } catch (error) {
      console.error('Error saving candidate process steps to Firestore:', error);
    }
  };

  // Toggle a candidate process step: incomplete → date modal, complete → undo
  const handleCandidateProcessStepClick = (stepId) => {
    const stepData = candidateProcessSteps[stepId];
    if (stepData?.completed) {
      handleMarkCandidateStepIncomplete(stepId);
    } else {
      setSelectedCandidateStep(stepId);
      setCandidateStepCompletionDate('');
      setShowCandidateProcessModal(true);
    }
  };

  const handleMarkCandidateStepComplete = async () => {
    if (!selectedCandidateStep || !candidateStepCompletionDate || !selectedCandidate) return;

    const updatedProcessSteps = {
      ...candidateProcessSteps,
      [selectedCandidateStep]: {
        completed: true,
        completedDate: candidateStepCompletionDate,
        completedAt: new Date().toISOString()
      }
    };

    setCandidateProcessSteps(updatedProcessSteps);
    setSelectedCandidate((prev) => ({ ...prev, processSteps: updatedProcessSteps }));
    setShowCandidateProcessModal(false);
    setSelectedCandidateStep(null);
    setCandidateStepCompletionDate('');

    await saveCandidateProcessStepsToFirestore(selectedCandidate.id, updatedProcessSteps);
  };

  const handleMarkCandidateStepIncomplete = async (stepId) => {
    if (!selectedCandidate) return;

    const updatedProcessSteps = {
      ...candidateProcessSteps,
      [stepId]: { completed: false, completedDate: null, completedAt: null }
    };

    setCandidateProcessSteps(updatedProcessSteps);
    setSelectedCandidate((prev) => ({ ...prev, processSteps: updatedProcessSteps }));
    await saveCandidateProcessStepsToFirestore(selectedCandidate.id, updatedProcessSteps);
  };

  const tabs = [
    { id: 'timeline', name: 'Timeline', icon: <FaClock /> },
    { id: 'candidates', name: 'Candidates', icon: <FaUsers /> },
    { id: 'notes', name: 'Notes', icon: <FaEdit /> }
  ];

  const renderTimelineTab = () => {
    if (!position) {
      return (
        <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <p>Loading timeline...</p>
        </div>
      );
    }

    const timelineStepData = [
      {
        id: 'define-requirements',
        title: 'Define Role Requirements',
        description: 'Establish clear job responsibilities, skills, and experience requirements',
        defaultCompleted: true,
        defaultDate: position.requestDate
      },
      {
        id: 'create-posting',
        title: 'Create Posting',
        description: 'Develop compelling job description and post to relevant platforms',
        statusDependent: false
      },
      {
        id: 'screen-applicants',
        title: 'Screen Applicants',
        description: 'Review resumes and applications to identify qualified candidates',
        statusDependent: false
      },
      {
        id: 'pre-screen-interview',
        title: 'Pre-Screen Interview',
        description: 'Initial phone or video interview to assess basic qualifications',
        statusDependent: false
      },
      {
        id: 'company-interview',
        title: 'Company Interview',
        description: 'In-person or virtual interview with team members and stakeholders',
        statusDependent: false
      },
      {
        id: 'decision-offer',
        title: 'Decision & Offer',
        description: 'Final evaluation and job offer to selected candidate',
        statusDependent: false
      }
    ];

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hiring Timeline - Left Half */}
        <div className={`p-6 rounded-lg shadow-md border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h3 className={`text-lg font-semibold mb-6 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Hiring Timeline
          </h3>
          <div className="space-y-6">
            {timelineStepData.map((step, index) => {
              const stepData = timelineSteps[step.id];
              const isCompleted = stepData?.completed || (step.defaultCompleted && !stepData);
              const completionDate = stepData?.completedDate || (step.defaultDate && !stepData ? step.defaultDate : null);
              const isClickable = !step.statusDependent;
              
              return (
                <div key={step.id} className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div 
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isCompleted 
                          ? (darkMode ? 'bg-green-900' : 'bg-green-100')
                          : (darkMode ? 'bg-gray-700' : 'bg-gray-100')
                      } ${isClickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                      onClick={isClickable ? () => handleTimelineStepClick(step.id) : undefined}
                      title={isClickable ? 'Click to mark as complete' : ''}
                    >
                      {isCompleted ? (
                        <FaCheckCircle className="text-green-500" />
                      ) : (
                        <FaClock className="text-gray-400" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`text-sm font-medium ${
                        isCompleted 
                          ? (darkMode ? 'text-green-200' : 'text-green-800')
                          : (darkMode ? 'text-white' : 'text-gray-900')
                      }`}>
                        {step.title}
                      </span>
                      {isCompleted && completionDate && (
                        <span className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                          {new Date(completionDate).toLocaleDateString()}
                        </span>
                      )}
                      {isCompleted && stepData && (
                        <button
                          onClick={() => handleMarkStepIncomplete(step.id)}
                          className={`text-xs px-2 py-1 rounded ${
                            darkMode 
                              ? 'text-red-300 hover:bg-red-900' 
                              : 'text-red-600 hover:bg-red-100'
                          } transition-colors`}
                          title="Mark as incomplete"
                        >
                          Undo
                        </button>
                      )}
                    </div>
                    <p className={`text-sm ${
                      isCompleted 
                        ? (darkMode ? 'text-green-200' : 'text-green-700')
                        : (darkMode ? 'text-gray-400' : 'text-gray-600')
                    }`}>
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Position Details - Right Half */}
        <div className={`p-6 rounded-lg shadow-md border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex justify-between items-center mb-6">
            <div className="flex-1"></div>
            <h3 className={`text-lg font-semibold text-center flex-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Posting Requirements
            </h3>
            <div className="flex-1 flex justify-end">
              <button
                onClick={handleEditDescription}
                className={`flex items-center px-3 py-1 text-sm rounded-md transition-colors ${
                  darkMode 
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <FaEdit className="w-4 h-4 mr-1" />
                Edit
              </button>
            </div>
          </div>
          
          <div className={`p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {position.description || 'No job description provided.'}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderCandidatesTab = () => (
    <div className="space-y-6">
      <div className={`overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div className="flex-1"></div>
          <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
            Candidates
          </h3>
          <div className="flex-1"></div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Candidate
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Feedback
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Stage
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Action
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Score
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}>
                  Applied
                </th>
              </tr>
            </thead>
            <tbody className={`${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {candidates.length > 0 ? (
                candidates.map((candidate) => (
                  <tr 
                    key={candidate.id} 
                    className={`${candidate.isFavorite ? (darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50') : ''} ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} cursor-pointer transition-colors duration-200`}
                    onClick={() => handleCandidateClick(candidate)}
                  >
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {candidate.name}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {candidate.feedbackRating ? (
                        <div className="flex items-center">
                          <div className="flex mr-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <FaStar 
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= candidate.feedbackRating
                                    ? 'text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-xs">
                            {candidate.feedbackRating}/5
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">No rating</span>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        getCandidateStage(candidate) === 'Decision Offer' ? 'bg-green-100 text-green-800' :
                        getCandidateStage(candidate) === 'Company Interview' ? 'bg-blue-100 text-blue-800' :
                        getCandidateStage(candidate) === 'Pre Screen Interview' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {getCandidateStage(candidate)}
                      </span>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {candidate.actionStatus ? (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          candidate.actionStatus === 'rejected' 
                            ? 'bg-red-100 text-red-800' 
                            : candidate.actionStatus === 'continue'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {candidate.actionStatus === 'rejected' ? 'Rejected' : 
                           candidate.actionStatus === 'continue' ? 'Continue' : 
                           candidate.actionStatus}
                        </span>
                      ) : (
                        <span className="text-gray-400">No action</span>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      <div className="flex items-center">
                        <span className="mr-2">{getCandidateScore(candidate)}</span>
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar 
                              key={star}
                              className={`w-3 h-3 ${
                                star <= (candidate.ratings?.overall || 0)
                                  ? 'text-yellow-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {candidate.appliedDate ? new Date(candidate.appliedDate).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className={`px-6 py-12 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <FaUsers className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium mb-2">No Candidates Yet</p>
                    <p>Click the + button to add your first candidate.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderNotesTab = () => (
    <div className={`rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 w-full max-w-full overflow-hidden`}>
      <div className="space-y-6 w-full">
        {/* Notes Header with Add Button */}
        <div className="flex justify-between items-center">
          <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
            Notes & Updates
          </h3>
          <button
            onClick={() => setShowAddNoteModal(true)}
            className={`p-2 text-white ${darkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-600 hover:bg-red-700'} rounded-full transition-colors duration-200`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        {/* Notes List */}
        <div className="space-y-4">
          {notes?.length > 0 ? (
            notes.map((note, index) => (
              <div key={note.id || index} className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg p-4 w-full max-w-full border`}>
                <div className="flex items-start justify-between w-full">
                  <div className="flex items-start space-x-3 flex-1 min-w-0 max-w-full">
                    <div className={`w-8 h-8 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'} rounded-full flex items-center justify-center overflow-hidden flex-shrink-0`}>
                      {note.author?.photoURL ? (
                        <img 
                          src={note.author.photoURL} 
                          alt={note.author.name}
                          className="w-full h-full object-cover"
                        />
                      ) : note.author?.name === 'System' ? (
                        <img 
                          src="/systemicon.png" 
                          alt="System"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {note.author?.name?.split(' ').map(n => n[0]).join('') || 'U'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 max-w-full overflow-hidden">
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {note.author?.name || 'Unknown User'}
                        </span>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {note.date || new Date(note.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} whitespace-pre-wrap mt-1 break-words overflow-wrap-anywhere`}>
                        {note.content}
                      </p>
                    </div>
                  </div>
                  {note.author?.uid === currentUser?.uid && (
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className={`${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'} ml-2`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p>No notes yet. Add your first note to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="relative min-h-screen p-6 text-gray-900 dark:text-white">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <div className="flex justify-center items-center py-12">
            <BBALoader size={48} />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="relative min-h-screen p-6 text-gray-900 dark:text-white">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center py-12">
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {error}
            </h2>
            <button
              onClick={() => navigate('/portal/hr/hiring')}
              className={`px-4 py-2 rounded-lg ${
                darkMode 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              Back to Hiring
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No position found
  if (!position) {
    return (
      <div className="relative min-h-screen p-6 text-gray-900 dark:text-white">
        <PortalBackground />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center py-12">
            <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Position not found
            </h2>
            <button
              onClick={() => navigate('/portal/hr/hiring')}
              className={`px-4 py-2 rounded-lg ${
                darkMode 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
            >
              Back to Hiring
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen p-6 text-gray-900 dark:text-white">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 relative">
          <div className="flex-1 flex items-center">
            <BackButton onClick={handleBack} />
          </div>
          <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
            <FaClipboardList className={`text-4xl mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
            <h1 className={`text-3xl font-bold tracking-tight text-center text-gray-900 dark:text-white`}>
              {position.title}
            </h1>
            <button
              onClick={() => setShowStatusModal(true)}
              className={`ml-4 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 hover:scale-105 ${
                position.status === 'Requested'
                  ? darkMode ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-purple-500 hover:bg-purple-600 text-white'
                  : position.status === 'Pending'
                  ? darkMode ? 'bg-yellow-600 hover:bg-yellow-700 text-white' : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                  : position.status === 'In Progress'
                  ? darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                  : position.status === 'Completed'
                  ? darkMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-500 hover:bg-green-600 text-white'
                  : darkMode ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-gray-500 hover:bg-gray-600 text-white'
              }`}
              title="Click to change status"
            >
              {position.status}
            </button>
          </div>
          <div className="flex-1 flex justify-end">
            <div className="flex space-x-2">
              {/* Cancel Request button moved to status modal */}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className={`p-6 rounded-lg shadow-md border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex flex-col items-center text-center">
              <div className={`p-3 rounded-lg mb-4 ${
                darkMode ? 'bg-blue-900' : 'bg-blue-100'
              }`}>
                <FaUsers className={`text-2xl ${darkMode ? 'text-blue-200' : 'text-blue-600'}`} />
              </div>
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Total Applications
              </p>
              <p className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                {candidates.length}
              </p>
            </div>
          </div>

          <div className={`p-6 rounded-lg shadow-md border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex flex-col items-center text-center">
              <div className={`p-3 rounded-lg mb-4 ${
                darkMode ? 'bg-green-900' : 'bg-green-100'
              }`}>
                <FaUserPlus className={`text-2xl ${darkMode ? 'text-green-200' : 'text-green-600'}`} />
              </div>
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Active Candidates
              </p>
              <p className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                {candidates.filter(c => c.actionStatus === 'continue').length}
              </p>
            </div>
          </div>

          <div className={`p-6 rounded-lg shadow-md border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex flex-col items-center text-center">
              <div className={`p-3 rounded-lg mb-4 ${
                darkMode ? 'bg-purple-900' : 'bg-purple-100'
              }`}>
                <FaCalendarAlt className={`text-2xl ${darkMode ? 'text-purple-200' : 'text-purple-600'}`} />
              </div>
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Interviews
              </p>
              <p className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                {candidates.filter(c => {
                  const processSteps = c.processSteps || {};
                  return processSteps['pre-screen-interview']?.completed || processSteps['company-interview']?.completed;
                }).length}
              </p>
            </div>
          </div>

          <div className={`p-6 rounded-lg shadow-md border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex flex-col items-center text-center">
              <div className={`p-3 rounded-lg mb-4 ${
                darkMode ? 'bg-yellow-900' : 'bg-yellow-100'
              }`}>
                <FaClock className={`text-2xl ${darkMode ? 'text-yellow-200' : 'text-yellow-600'}`} />
              </div>
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Days Posted
              </p>
              <p className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                {position.daysPosted}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-6`}>
          <nav className="-mb-px flex justify-center space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? `border-red-500 ${darkMode ? 'text-red-400' : 'text-red-600'}`
                    : `border-transparent ${
                        darkMode 
                          ? 'text-gray-400 hover:text-gray-300 hover:border-gray-600' 
                          : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'timeline' && renderTimelineTab()}
          {activeTab === 'candidates' && renderCandidatesTab()}
          {activeTab === 'notes' && renderNotesTab()}
        </div>
      </div>

      {/* Add Note Modal */}
      {showAddNoteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 max-w-lg w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Add New Note
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => {
                    setShowAddNoteModal(false);
                    setNewNote('');
                  }}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <textarea
                className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                rows="4"
                placeholder="Write your note here..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddNoteModal(false);
                    setNewNote('');
                  }}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNote}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Add Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Details Modal */}
      {showCandidateModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 max-w-6xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto`}>
            <div className="flex justify-between items-center mb-6 gap-3">
              <div className="flex-1 flex justify-start">
                <button
                  onClick={() => setShowCandidateModal(false)}
                  className={`p-2 rounded-full transition-colors ${
                    darkMode
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  title="Close"
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
              <h3 className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white text-center`}>
                {selectedCandidate.name}
              </h3>
              <div className="flex-1 flex justify-end items-center gap-2">
                <button
                  onClick={() => handleActionSelect('reject')}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedAction === 'reject'
                      ? 'bg-red-600 text-white'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                  title="Reject candidate"
                >
                  <FaTimesCircle className="w-4 h-4 mr-1.5" />
                  Reject
                </button>
                <button
                  onClick={() => handleActionSelect('continue')}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedAction === 'continue'
                      ? 'bg-green-600 text-white'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title="Continue candidate"
                >
                  <FaCheckCircle className="w-4 h-4 mr-1.5" />
                  Continue
                </button>
                <button
                  onClick={() => handleToggleFavorite(selectedCandidate.id)}
                  className={`p-2 transition-colors duration-200 ${
                    selectedCandidate.isFavorite
                      ? 'text-red-500 hover:text-red-600'
                      : (darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-500 hover:text-red-500')
                  }`}
                  title={selectedCandidate.isFavorite ? "Remove from Favorites" : "Mark as Favorite"}
                >
                  <FaHeart className={`h-5 w-5 ${selectedCandidate.isFavorite ? 'fill-current' : ''}`} />
                </button>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Basic Information and Candidate Ratings - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className={`p-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm`}>
                  <h5 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Basic Information
                  </h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Full Name
                      </label>
                      <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedCandidate.name}
                      </p>
                    </div>
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Applied Date
                      </label>
                      <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedCandidate.appliedDate ? new Date(selectedCandidate.appliedDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Email
                      </label>
                      <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedCandidate.email || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Phone
                      </label>
                      <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedCandidate.phone || 'Not specified'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Resume Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                    <div className="text-center mb-4">
                      <h6 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Resume
                      </h6>
                    </div>
                    <div className={`p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                      {selectedCandidate.resumeUrl ? (
                        <div className="flex items-center justify-center gap-2">
                          <a
                            href={selectedCandidate.resumeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center px-3 py-2 ${darkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'} rounded-lg transition-colors duration-200`}
                            title="View resume in a new tab"
                          >
                            <FaExternalLinkAlt className="w-4 h-4 mr-2" />
                            View
                          </a>
                          <a
                            href={selectedCandidate.resumeUrl}
                            download={selectedCandidate.resumeFileName || `${selectedCandidate.name}_Resume.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center px-3 py-2 ${darkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'} rounded-lg transition-colors duration-200`}
                            title="Download resume"
                          >
                            <FaDownload className="w-4 h-4 mr-2" />
                            Download
                          </a>
                        </div>
                      ) : (
                        <div className="text-center">
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            No resume uploaded
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Star Ratings — Overall and Culture stacked as separate cards */}
                <div className="space-y-6">
                  <div className={`p-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm`}>
                    <h5 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Overall
                    </h5>
                    <div className="flex justify-center">
                      {renderStarRating('overall', candidateRatings.overall)}
                    </div>
                  </div>
                  <div className={`p-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm`}>
                    <h5 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Culture
                    </h5>
                    <div className="flex justify-center">
                      {renderStarRating('culture', candidateRatings.culture)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Feedback - full width (actions moved to header) */}
              {selectedCandidate.feedbackRating && (
                <div className="grid grid-cols-1 gap-6">
                  <div className={`p-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm`}>
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex-1"></div>
                      <h5 className={`text-xl font-semibold text-center flex-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Company Feedback
                      </h5>
                      <div className="flex-1 flex justify-end">
                        <button
                          onClick={handleOpenFeedback}
                          className={`p-2 rounded-full transition-colors duration-200 ${
                            darkMode
                              ? 'bg-gray-600 hover:bg-gray-500 text-gray-300 hover:text-white'
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800'
                          }`}
                          title="Edit Feedback"
                        >
                          <FaEdit className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="text-center mb-4">
                      <div className="flex justify-center space-x-1 mb-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <FaStar
                            key={star}
                            className={`w-6 h-6 ${
                              star <= selectedCandidate.feedbackRating
                                ? 'text-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {selectedCandidate.feedbackRating === 1 ? 'Poor' :
                         selectedCandidate.feedbackRating === 2 ? 'Fair' :
                         selectedCandidate.feedbackRating === 3 ? 'Good' :
                         selectedCandidate.feedbackRating === 4 ? 'Very Good' : 'Excellent'}
                      </p>
                    </div>
                    {selectedCandidate.feedbackNote && (
                      <div className={`p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {selectedCandidate.feedbackNote}
                        </p>
                      </div>
                    )}
                    {selectedCandidate.feedbackUpdatedAt && (
                      <p className={`text-xs text-center mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Updated: {new Date(selectedCandidate.feedbackUpdatedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Hiring Process Steps and Looking For - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Hiring Process Steps */}
                <div className={`p-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm`}>
                  <h5 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Hiring Process Steps
                  </h5>
                  <div className="space-y-2">
                    {(() => {
                      const candidateProcessStepData = [
                        {
                          id: 'applied',
                          title: 'Applied',
                          description: 'Candidate submitted application',
                          defaultCompleted: true,
                          defaultDate: selectedCandidate.appliedDate
                        },
                        {
                          id: 'pre-screen-interview',
                          title: 'Pre-Screen Interview',
                          description: 'Initial phone or video interview completed',
                          statusDependent: false
                        },
                        {
                          id: 'company-interview',
                          title: 'Company Interview',
                          description: 'In-person or virtual interview with team members',
                          statusDependent: false
                        },
                        {
                          id: 'decision-offer',
                          title: 'Decision & Offer',
                          description: 'Final evaluation and job offer decision',
                          statusDependent: false
                        }
                      ];

                      const nextStepIndex = candidateProcessStepData.findIndex((step) => {
                        const sd = candidateProcessSteps[step.id];
                        return !(sd?.completed || (step.defaultCompleted && !sd));
                      });

                      return candidateProcessStepData.map((step, index) => {
                        const stepData = candidateProcessSteps[step.id];
                        const isCompleted = stepData?.completed || (step.defaultCompleted && !stepData);
                        const completionDate = stepData?.completedDate || (step.defaultDate && !stepData ? step.defaultDate : null);
                        const isClickable = !step.statusDependent;
                        const isCurrent = index === nextStepIndex;

                        return (
                          <div
                            key={step.id}
                            className={`flex items-start space-x-4 rounded-lg p-3 transition-colors ${
                              isCurrent
                                ? (darkMode ? 'bg-red-900/20 ring-2 ring-red-500' : 'bg-red-50 ring-2 ring-red-500')
                                : ''
                            }`}
                          >
                            <div className="flex-shrink-0">
                              <button
                                type="button"
                                onClick={isClickable ? () => handleCandidateProcessStepClick(step.id) : undefined}
                                disabled={!isClickable}
                                title={isCompleted ? 'Click to mark as incomplete' : 'Click to mark this step complete'}
                                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500 ${
                                  darkMode ? 'focus-visible:ring-offset-gray-700' : 'focus-visible:ring-offset-white'
                                } ${isClickable ? 'cursor-pointer' : 'cursor-default'} ${
                                  isCompleted
                                    ? 'text-green-500 hover:text-green-600'
                                    : isCurrent
                                    ? (darkMode ? 'text-red-400 hover:text-green-500' : 'text-red-500 hover:text-green-600')
                                    : (darkMode ? 'text-gray-500 hover:text-green-500' : 'text-gray-400 hover:text-green-600')
                                }`}
                              >
                                {isCompleted ? (
                                  <FaCheckCircle className="w-7 h-7" />
                                ) : (
                                  <FaRegCircle className="w-7 h-7" />
                                )}
                              </button>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center flex-wrap gap-2 mb-1">
                                <span className={`text-sm font-medium ${
                                  isCompleted
                                    ? (darkMode ? 'text-green-200' : 'text-green-800')
                                    : isCurrent
                                    ? (darkMode ? 'text-red-300' : 'text-red-700')
                                    : (darkMode ? 'text-white' : 'text-gray-900')
                                }`}>
                                  {step.title}
                                </span>
                                {isCurrent && (
                                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-600 text-white">
                                    Next — check off when done
                                  </span>
                                )}
                                {isCompleted && completionDate && (
                                  <span className={`text-xs ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                                    {new Date(completionDate).toLocaleDateString()}
                                  </span>
                                )}
                                {isCompleted && stepData && (
                                  <button
                                    onClick={() => handleMarkCandidateStepIncomplete(step.id)}
                                    className={`text-xs px-2 py-1 rounded ${
                                      darkMode
                                        ? 'text-red-300 hover:bg-red-900'
                                        : 'text-red-600 hover:bg-red-100'
                                    } transition-colors`}
                                    title="Mark as incomplete"
                                  >
                                    Undo
                                  </button>
                                )}
                              </div>
                              <p className={`text-xs ${
                                isCompleted
                                  ? (darkMode ? 'text-green-200' : 'text-green-700')
                                  : (darkMode ? 'text-gray-400' : 'text-gray-600')
                              }`}>
                                {step.description}
                              </p>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Looking For */}
                <div className={`p-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm`}>
                  <h5 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Looking For
                  </h5>
                  
                  {/* Salary and Start Date Row */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Salary
                      </label>
                      <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedCandidate.salary || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <label className={`text-sm font-medium text-center block ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Start Date
                      </label>
                      <p className={`text-sm text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedCandidate.startDate || 'Not specified'}
                      </p>
                    </div>
                  </div>

                  {/* Additional Items */}
                  <div>
                    <label className={`text-sm font-medium text-center block mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      Additional Items
                    </label>
                    <div className={`p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50`}>
                      <p className={`text-sm text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {selectedCandidate.additionalItems || 'No additional items specified'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowCandidateModal(false)}
                  className={`px-6 py-2 rounded-lg text-sm font-medium ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timeline Step Completion Modal */}
      {showTimelineModal && selectedStep && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 max-w-md w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Mark Step Complete
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => {
                    setShowTimelineModal(false);
                    setSelectedStep(null);
                    setStepCompletionDate('');
                  }}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Completion Date *
                </label>
                <input
                  type="date"
                  className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                  value={stepCompletionDate}
                  onChange={(e) => setStepCompletionDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowTimelineModal(false);
                    setSelectedStep(null);
                    setStepCompletionDate('');
                  }}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkStepComplete}
                  disabled={!stepCompletionDate}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    stepCompletionDate
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  Mark Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Process Step Completion Modal */}
      {showCandidateProcessModal && selectedCandidateStep && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 max-w-md w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Mark Step Complete
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => {
                    setShowCandidateProcessModal(false);
                    setSelectedCandidateStep(null);
                    setCandidateStepCompletionDate('');
                  }}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Completion Date *
                </label>
                <input
                  type="date"
                  className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                  value={candidateStepCompletionDate}
                  onChange={(e) => setCandidateStepCompletionDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCandidateProcessModal(false);
                    setSelectedCandidateStep(null);
                    setCandidateStepCompletionDate('');
                  }}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkCandidateStepComplete}
                  disabled={!candidateStepCompletionDate}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    candidateStepCompletionDate
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  Mark Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Description Modal */}
      {showEditDescriptionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 max-w-2xl w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Edit Job Description
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={handleCancelEditDescription}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                  Job Description
                </label>
                <textarea
                  className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                  rows="8"
                  placeholder="Enter job description..."
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelEditDescription}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDescription}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Position Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 max-w-md w-full mx-4`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex-1"></div>
              <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'} text-center flex-1`}>
                Cancel Position Request
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to cancel this position request? This action cannot be undone and will remove all associated data.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCancelPosition}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Cancel Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && selectedCandidate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 max-w-2xl w-full mx-4`}>
            <div className="flex justify-between items-center mb-6">
              <div className="flex-1"></div>
              <h3 className={`text-xl font-bold tracking-tight text-gray-900 dark:text-white text-center flex-1`}>
                Company Rating - {selectedCandidate.name}
              </h3>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={handleCancelFeedback}
                  className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="space-y-6">
              {/* Star Rating */}
              <div className="text-center">
                <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Rate this candidate (1-5 stars)
                </h4>
                <div className="flex justify-center space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setFeedbackRating(star)}
                      className={`transition-colors duration-200 ${
                        star <= feedbackRating 
                          ? 'text-yellow-400 hover:text-yellow-500' 
                          : 'text-gray-300 hover:text-yellow-400'
                      }`}
                    >
                      <FaStar className="w-8 h-8" />
                    </button>
                  ))}
                </div>
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {feedbackRating === 0 ? 'No rating selected' : 
                   feedbackRating === 1 ? 'Poor' :
                   feedbackRating === 2 ? 'Fair' :
                   feedbackRating === 3 ? 'Good' :
                   feedbackRating === 4 ? 'Very Good' : 'Excellent'}
                </p>
              </div>

              {/* Feedback Note */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Feedback Note
                </label>
                <textarea
                  className={`w-full p-3 border ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'}`}
                  rows="4"
                  placeholder="Write your feedback about this candidate..."
                  value={feedbackNote}
                  onChange={(e) => setFeedbackNote(e.target.value)}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCancelFeedback}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFeedback}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Selection Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 max-w-md w-full mx-4`}>
            <div className="flex justify-center items-center mb-6">
              <h3 className={`text-xl font-bold tracking-tight text-gray-900 dark:text-white`}>
                Update Position Status
              </h3>
            </div>
            
            <div className="space-y-4">
              <p className={`text-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Select the current status for this position:
              </p>
              
              <div className="space-y-3">
                {['Pending', 'In Progress', 'Completed'].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusUpdate(status)}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors duration-200 ${
                      position.status === status
                        ? (status === 'Pending' 
                            ? 'bg-yellow-500 text-white' 
                            : status === 'In Progress'
                            ? 'bg-blue-500 text-white'
                            : 'bg-green-500 text-white')
                        : (status === 'Pending'
                            ? darkMode ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                            : status === 'In Progress'
                            ? darkMode ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                            : darkMode ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-green-100 text-green-800 hover:bg-green-200')
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
              
              <div className="flex justify-between items-center mt-6">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setShowCancelModal(true);
                  }}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    darkMode 
                      ? 'bg-red-600 text-white hover:bg-red-700' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  <FaTimes className="w-4 h-4 mr-2 inline" />
                  Cancel Request
                </button>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:text-gray-100' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PositionHiringOverview;
